import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { ExternalLink, Upload } from 'lucide-react';
import BenevolCheckLogo from '../components/BenevolCheckLogo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, getAuthHeader } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'volunteer',
    association_name: '',
    association_address: '',
    accepted_cgu: false
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState(null);

  const HELLOASSO_PAYMENT_URL = "https://www.helloasso.com/associations/france-benevolat-cote-d-or/evenements/benevol-check";
  const HELLOASSO_QR_CODE = "https://customer-assets.emergentagent.com/job_42c7d9e2-a406-42dd-8f91-3b2fe3fb0f5a/artifacts/huoscq39_qrcode%20%281%29.png";

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille (max 2 Mo)
      if (file.size > 2 * 1024 * 1024) {
        setError('Le logo ne doit pas dépasser 2 Mo');
        return;
      }
      // Vérifier le type
      if (!['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'].includes(file.type)) {
        setError('Format non supporté. Utilisez PNG, JPG, GIF ou WebP');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const uploadLogo = async (token) => {
    if (!logoFile) return;
    
    try {
      const formDataLogo = new FormData();
      formDataLogo.append('file', logoFile);
      
      await axios.post(`${API}/users/me/logo`, formDataLogo, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (err) {
      console.error('Erreur upload logo:', err);
      // Ne pas bloquer l'inscription si l'upload échoue
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Vérifier l'acceptation des CGU
    if (!formData.accepted_cgu) {
      setError('Vous devez accepter les CGU et la politique de confidentialité');
      return;
    }
    
    setLoading(true);

    const result = await register(formData);
    
    if (result.success) {
      // Upload du logo si présent (pour les associations)
      if (logoFile && result.token) {
        await uploadLogo(result.token);
      }
      navigate('/dashboard');
    } else {
      // Si c'est un responsable associatif, montrer la page de paiement
      if (formData.role === 'association_admin' && result.error.includes('attente de validation')) {
        // Upload du logo même si le compte est en attente
        if (logoFile && result.token) {
          await uploadLogo(result.token);
        }
        setShowPayment(true);
      } else {
        setError(result.error);
      }
    }
    
    setLoading(false);
  };

  // Page de paiement HelloAsso pour les responsables associatifs
  if (showPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg" data-testid="payment-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <BenevolCheckLogo size="lg" />
            </div>
            <CardTitle className="text-2xl text-green-600">✅ Inscription enregistrée !</CardTitle>
            <CardDescription className="text-base mt-2">
              Votre demande d'inscription a bien été prise en compte.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-800">
                <strong>Dernière étape :</strong> Pour finaliser votre inscription et activer votre compte, 
                veuillez régler la cotisation annuelle de <strong>10 €</strong>.
              </AlertDescription>
            </Alert>

            <div className="text-center space-y-4">
              <p className="text-gray-600">Scannez ce QR code ou cliquez sur le bouton ci-dessous :</p>
              
              <div className="flex justify-center">
                <img 
                  src={HELLOASSO_QR_CODE} 
                  alt="QR Code paiement HelloAsso"
                  className="w-48 h-48 border-2 border-gray-200 rounded-lg shadow-md"
                />
              </div>

              <a 
                href={HELLOASSO_PAYMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                data-testid="payment-link"
              >
                <ExternalLink size={20} />
                Régler ma cotisation (10 €)
              </a>

              <p className="text-sm text-gray-500">
                Paiement sécurisé via HelloAsso
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>⏳ Que se passe-t-il ensuite ?</strong><br />
                Après votre paiement, un administrateur validera votre compte dans les 24-48h. 
                Vous recevrez un email de confirmation dès que votre compte sera activé.
              </p>
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Link 
              to="/login" 
              className="text-sm text-center text-gray-600 hover:underline"
            >
              Retour à la connexion
            </Link>
            <Link 
              to="/" 
              className="text-sm text-center text-gray-600 hover:underline"
            >
              ← Retour à l'accueil
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="register-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <BenevolCheckLogo size="lg" />
          </div>
          <CardTitle className="text-2xl">Inscription</CardTitle>
          <CardDescription>Créez votre compte Bénévol'Check</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive" data-testid="error-alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet</Label>
              <Input
                id="name"
                type="text"
                placeholder="Jean Dupont"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="votre@email.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                minLength={6}
                data-testid="password-input"
              />
            </div>
            
            <div className="space-y-3">
              <Label>Je suis :</Label>
              <RadioGroup 
                value={formData.role} 
                onValueChange={(value) => setFormData({...formData, role: value})}
                data-testid="role-radio-group"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="volunteer" id="volunteer" data-testid="role-volunteer" />
                  <Label htmlFor="volunteer" className="font-normal cursor-pointer">
                    Bénévole
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="association_admin" id="admin" data-testid="role-admin" />
                  <Label htmlFor="admin" className="font-normal cursor-pointer">
                    Responsable associatif
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            {formData.role === 'association_admin' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="association">Nom de l'association</Label>
                  <Input
                    id="association"
                    type="text"
                    placeholder="Association XYZ"
                    value={formData.association_name}
                    onChange={(e) => setFormData({...formData, association_name: e.target.value})}
                    required
                    data-testid="association-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="association_address">Adresse de l'association (Ville)</Label>
                  <Input
                    id="association_address"
                    type="text"
                    placeholder="Paris, Dijon, Lyon..."
                    value={formData.association_address}
                    onChange={(e) => setFormData({...formData, association_address: e.target.value})}
                    required
                    data-testid="association-address-input"
                  />
                  <p className="text-xs text-gray-500">
                    Cette ville sera utilisée comme lieu de signature des attestations
                  </p>
                </div>
                
                {/* Upload du logo */}
                <div className="space-y-2">
                  <Label htmlFor="logo">Logo de l'association (optionnel)</Label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                      <Upload size={18} />
                      <span className="text-sm">{logoFile ? 'Changer' : 'Choisir un fichier'}</span>
                      <input
                        id="logo"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                        onChange={handleLogoChange}
                        className="hidden"
                        data-testid="logo-input"
                      />
                    </label>
                    {logoPreview && (
                      <img 
                        src={logoPreview} 
                        alt="Aperçu du logo" 
                        className="w-12 h-12 object-contain border rounded"
                      />
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, GIF ou WebP. Max 2 Mo. Le logo apparaîtra sur vos attestations.
                  </p>
                </div>
              </>
            )}

            {/* Case à cocher CGU */}
            <div className="flex items-start gap-2 pt-2">
              <Checkbox
                id="accepted_cgu"
                checked={formData.accepted_cgu}
                onCheckedChange={(checked) => setFormData({...formData, accepted_cgu: checked})}
                data-testid="cgu-checkbox"
              />
              <Label htmlFor="accepted_cgu" className="text-sm text-gray-600 cursor-pointer leading-tight">
                J'accepte les{' '}
                <Link to="/cgu" target="_blank" className="text-indigo-600 hover:underline">
                  Conditions Générales d'Utilisation
                </Link>
                {' '}et la{' '}
                <Link to="/confidentialite" target="_blank" className="text-indigo-600 hover:underline">
                  Politique de Confidentialité
                </Link>
              </Label>
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Inscription...' : 'S\'inscrire'}
            </Button>
            
            <div className="text-sm text-center text-gray-600">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-indigo-600 hover:underline" data-testid="login-link">
                Se connecter
              </Link>
            </div>
            
            <Link 
              to="/" 
              className="text-sm text-center text-gray-600 hover:underline"
              data-testid="back-link"
            >
              ← Retour à l'accueil
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default RegisterPage;